import type { ComponentProps } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';

import { Input } from '@/components/ui/input';
import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';

/**
 * A text field with the planner's own sent mail behind it (EVE-88).
 *
 * Used twice in the Add Vendor dialog — once for the business name, once for
 * the email address — because the two are the same gesture from her side: she
 * types the first few letters of a florist she has already emailed, and the
 * field knows both halves. Which of the two she started in does not decide
 * what she can find; the caller passes the same list to both.
 *
 * Free text is the normal case, not the fallback: most vendors she adds have
 * never been emailed from this account. So the input's value belongs to the
 * caller's form state throughout, and nothing here narrows, validates or
 * rewrites what she typed. There is deliberately no empty-state row either —
 * a "no matches" line would tell every planner without a Gmail connection
 * about a feature she has not got, on every keystroke.
 *
 * Built on `ui/combobox.tsx` (base-ui) rather than hand-rolled, for the same
 * reason `VendorRecipientField` is: arrow-key navigation, the
 * highlight/selection split and the whole listbox ARIA contract are what the
 * primitive already ships, and they are the parts that are easy to get subtly
 * wrong.
 */

export interface GmailContact {
  email: string;
  displayName: string | null;
}

/**
 * One suggestion, in the same three-part shape as a row of the recipient
 * field's rolodex list: the name on top, the address beneath it in small muted
 * text. A planner should not have to learn that these are different things.
 *
 * A contact with no display name is the address *only*, rather than a blank
 * line above it — half this list is bare addresses, and a column of empty
 * first lines is worse than a shorter row.
 */
function ContactRow({ contact }: { contact: GmailContact }) {
  return (
    <ComboboxItem value={contact} className="items-start">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">
          {contact.displayName ?? contact.email}
        </span>
        {contact.displayName !== null && (
          <span className="block truncate text-xs text-muted-foreground">{contact.email}</span>
        )}
      </span>
    </ComboboxItem>
  );
}

/**
 * Switches off base-ui's client-side filtering.
 *
 * The list is already the server's answer to what she typed: ranked in Postgres
 * by how often and how recently she mailed each address, and capped at eight.
 * Filtering it again here would narrow that by different rules, and the two
 * would drift. An always-true predicate rather than `filter={null}` — both are
 * documented opt-outs, and a predicate says "every row the server sent belongs
 * on screen" where a null reads as a feature nobody got round to.
 *
 * At module scope so its identity is stable: base-ui memoises its filtered list
 * on `filter`, and a fresh arrow each render would invalidate that every time.
 */
const noClientFilter = () => true;

export default function ContactField({
  id,
  value,
  onValueChange,
  contacts,
  scanning,
  onPick,
  onBlur,
  type,
  autoFocus,
}: {
  /** Lands on the text input, so the caller can write `<Label htmlFor>`.
   *  Without this passthrough it is not merely awkward but impossible:
   *  base-ui generates the input's id and nothing outside can know it. */
  id: string;
  value: string;
  /** The planner typed. Only ever raised for her own typing — see the
   *  `input-change` gate below. */
  onValueChange: (next: string) => void;
  contacts: GmailContact[];
  /** A scan is running *right now*. The only state worth saying out loud,
   *  because it will be true and then stop being true. */
  scanning: boolean;
  onPick: (contact: GmailContact) => void;
  /** Passed through for the dialog's duplicate check. */
  onBlur?: () => void;
  type?: ComponentProps<'input'>['type'];
  autoFocus?: boolean;
}) {
  // Nothing to show is a closed popup, not an open one with a message in it.
  const waiting = contacts.length === 0 && scanning;
  const showContent = contacts.length > 0 || waiting;

  return (
    <Combobox
      items={contacts}
      // Typing is the only opener. base-ui would otherwise offer the list on a
      // click into an empty field, and the server answers an empty query with
      // the top of its ranking — which ranks by how often she mails someone,
      // so on a real mailbox those eight are her family, her accountant and
      // Send-to-Kindle, not vendors. A dropdown of the wrong eight people
      // dropping over the form unbidden is worse than no dropdown.
      //
      // It also buys back close-on-empty: base-ui's clear-to-close branch is
      // gated on this same flag, so clearing the field now dismisses the popup
      // instead of swapping it to that list.
      openOnInputClick={false}
      // Held at `null` on purpose: this field's text is the truth, and the
      // caller owns it. A sticky selection would paint a checkmark against a
      // row the planner has since typed past, and base-ui would re-assert that
      // row's label over her typing.
      value={null}
      onValueChange={(next: GmailContact | null) => {
        if (next) onPick(next);
      }}
      inputValue={value}
      onInputValueChange={(next: string, details) => {
        // Her own typing is the only thing that may move the form's value.
        // base-ui otherwise writes to a controlled input twice: it wipes it on
        // every close (`input-clear`) and overwrites it with the accepted
        // row's label on a press. The first would empty a half-typed vendor
        // name the moment she dismissed the dropdown to carry on with the
        // form; the second would put "Bloom Floral" in the *email* field.
        // Both are refused through base-ui's own documented opt-out, which
        // also skips its internal uncontrolled-value write.
        if (details.reason !== 'input-change') {
          details.cancel();
          return;
        }
        onValueChange(next);
      }}
      itemToStringLabel={(contact: GmailContact) => contact.displayName ?? contact.email}
      isItemEqualToValue={(a: GmailContact, b: GmailContact) => a.email === b.email}
      filter={noClientFilter}
    >
      <ComboboxPrimitive.Input
        id={id}
        autoFocus={autoFocus}
        onBlur={onBlur}
        // The app's own field, not the primitive's `InputGroup` shell: this has
        // to be indistinguishable from the inputs above and below it in the
        // same form, down to the focus treatment.
        render={<Input type={type} />}
      />

      {showContent && (
        <ComboboxContent>
          <ComboboxList>
            {(contact: GmailContact) => <ContactRow key={contact.email} contact={contact} />}
          </ComboboxList>
          {waiting && (
            // base-ui's own primitive for this, which carries `role="status"`
            // and `aria-live="polite"`. Rolling a plain `<p>` left the line
            // announced to nobody: a screen-reader user typed, heard silence,
            // and then had options appear with no explanation of the wait.
            //
            // Not a `ComboboxItem` either: there is nothing here to pick, and a
            // disabled option would have the listbox announce an item count
            // that is really a status line, in the arrow keys' way.
            <ComboboxPrimitive.Status className="px-2 py-1.5 text-sm text-muted-foreground">
              Looking through your sent mail…
            </ComboboxPrimitive.Status>
          )}
        </ComboboxContent>
      )}
    </Combobox>
  );
}
